import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { checkPoliciesAccepted, updatePoliciesAccepted } from '../services/userService';
import { createLogger } from '../utils/logger';
import { POLICY_PAGE_PATH_LIST } from '../components/policy/policyPaths';

const logger = createLogger('PolicyContext');

const MAX_POLICY_CHECK_RETRIES = 3;

const POLICY_SKIP_PATHS = [
  '/login',
  '/signup',
  '/pricing',
  '/forgot-password',
  '/sso',
  '/session-expired',
  ...POLICY_PAGE_PATH_LIST,
  '/error/404',
  '/error/500',
  '/error/503',
];

export interface PolicyContextType {
  // null = unknown (pre-auth, loading, or check errored). Providers gate data fetches on `=== true`.
  policiesAccepted: boolean | null;
  isSkippedPath: boolean;
  checking: boolean;
  checkError: boolean;
  retryCount: number;
  maxRetries: number;
  modalOpen: boolean;
  saving: boolean;
  acceptPolicies: () => Promise<void>;
  retry: () => void;
}

const PolicyContext = createContext<PolicyContextType | undefined>(undefined);

export const usePolicy = (): PolicyContextType => {
  const ctx = useContext(PolicyContext);
  if (!ctx) throw new Error('usePolicy must be used within a PolicyProvider');
  return ctx;
};

export const PolicyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, token, loading, noOrganization } = useAuth() as {
    currentUser: { id: string } | null;
    token: string | null;
    loading: boolean;
    noOrganization: boolean;
  };
  const location = useLocation();

  const [policiesAccepted, setPoliciesAccepted] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const [checkError, setCheckError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Only check once per user session (reset on user change)
  const checkedRef = useRef<string | null>(null);

  const isSkippedPath =
    POLICY_SKIP_PATHS.includes(location.pathname) ||
    location.pathname.startsWith('/auth/magic/');

  useEffect(() => {
    if (isSkippedPath) {
      // Skipped paths (login, SSO redirect, etc.) don't require a policy check;
      // treat as accepted so downstream providers are free to operate.
      setChecking(false);
      setPoliciesAccepted(true);
      return;
    }

    if (loading) return;

    // Users with no organization cannot accept policies — skip the check so the
    // policy modal never fires before OrganizationGuard shows the no-org wall.
    if (noOrganization) {
      setChecking(false);
      setPoliciesAccepted(null);
      return;
    }

    if (!currentUser || !token) {
      setChecking(false);
      setPoliciesAccepted(null);
      return;
    }

    if (checkedRef.current === currentUser.id) {
      setChecking(false);
      return;
    }

    const check = async () => {
      setCheckError(false);
      try {
        const accepted = await checkPoliciesAccepted(currentUser.id, token);
        checkedRef.current = currentUser.id;
        setPoliciesAccepted(accepted);
        if (!accepted) {
          setModalOpen(true);
        }
      } catch (err) {
        logger.error('Failed to check policy acceptance:', err);
        // Don't set checkedRef or policiesAccepted — block the user and let them retry.
        setCheckError(true);
        setPoliciesAccepted(null);
      } finally {
        setChecking(false);
      }
    };

    check();
  }, [currentUser, token, loading, noOrganization, isSkippedPath, retryCount]);

  // Reset on logout
  useEffect(() => {
    if (!currentUser) {
      checkedRef.current = null;
      setModalOpen(false);
      setCheckError(false);
      setRetryCount(0);
      setPoliciesAccepted(null);
    }
  }, [currentUser]);

  const retry = useCallback(() => {
    if (retryCount >= MAX_POLICY_CHECK_RETRIES) return;
    setChecking(true);
    setRetryCount((c) => c + 1);
  }, [retryCount]);

  const acceptPolicies = useCallback(async () => {
    if (!currentUser || !token) return;
    setSaving(true);
    try {
      await updatePoliciesAccepted(currentUser.id, token);
      setPoliciesAccepted(true);
      setModalOpen(false);
    } catch (err) {
      logger.error('Failed to save policy acceptance:', err);
      // Keep modal open — don't close on failure
    } finally {
      setSaving(false);
    }
  }, [currentUser, token]);

  const value: PolicyContextType = {
    policiesAccepted,
    isSkippedPath,
    checking,
    checkError,
    retryCount,
    maxRetries: MAX_POLICY_CHECK_RETRIES,
    modalOpen,
    saving,
    acceptPolicies,
    retry,
  };

  return <PolicyContext.Provider value={value}>{children}</PolicyContext.Provider>;
};
